import { z } from 'zod';

import { LocaleSchema } from '../common/locale/locale';
import { UuidSchema } from '../common/id/id';
import { PhoneNumberSchema } from '../common/phone-number/phone-number';
import { MemberPhotoSchema } from '../profiles/member-view/member-view';
import { PhotoAdvisorySchema } from '../verification/photo-advisory/photo-advisory';

import { GenderSchema } from './gender';
import { UserStatusSchema } from './user-status';

/** A date of birth: a calendar day, never an instant — `YYYY-MM-DD`. */
export const DateOfBirthSchema = z.iso.date();

/**
 * The user as every client sees themselves and as staff see a member row.
 *
 * There is **no `passwordHash` field, in any contract, ever** (N2): the column
 * is declared `hidden` on the repository, so it never reaches a response in the
 * first place — this schema is the second lock, not the only one.
 */
export const UserSchema = z.object({
  id: UuidSchema,
  email: z.email(),
  gender: GenderSchema,
  dob: DateOfBirthSchema,
  locale: LocaleSchema,
  status: UserStatusSchema,
  /**
   * The member's **own** "hide me" switch — theirs to turn on and off.
   *
   * Deliberately not the union of the two hiding facts. Everywhere else in the
   * platform they ARE folded together, by `UsersQueryResponder`, because no
   * consumer deciding whether to show somebody has any business knowing why the
   * answer is no. Here the reader is the member themselves, and a switch drawn
   * as "off" while their profile is invisible is a control lying to its owner.
   */
  isHidden: z.boolean(),
  /**
   * Hidden by moderation while reports are open against them
   * (`moderation.autoHideReportThreshold`, P19a).
   *
   * Read-only to every client: the member cannot turn it off, and the surface
   * that draws it explains why rather than offering a control that would 403.
   * It lifts on its own when the last open report is resolved — it is a holding
   * measure, never enforcement (a member actually taken out of service is
   * `suspended` or `banned`, which `status` already says).
   */
  isAutoHidden: z.boolean(),
  /**
   * Which of the two kinds of account this is (P27c1). **Staff = holds at least
   * one role whose key is not `member`.** Role-based, never permission-based: it
   * is what the staff list has always meant, it is one SQL predicate
   * (`holdsStaffRole`) and one actor predicate (`isStaffActor`), and it does not
   * go stale the day a custom role is emptied of permissions. A staff account
   * is never a member-population row — no picker, list, nomination pool, browse
   * batch, campaign audience or member-to-member view draws one.
   */
  isStaff: z.boolean(),
  /**
   * The mobile number in E.164, or `null` until the member gives one (P30b2).
   * A number sits here **unverified** until `phoneVerifiedAt` is set; the
   * platform never reaches a member on a number nobody proved.
   */
  phone: PhoneNumberSchema.nullable(),
  /**
   * When the address was proven by a code (P30a), or `null` while it is not.
   * Contact verification is these two timestamps, never a status (N9): the
   * account is created at sign-up as it always was, and the gate on the
   * `registered → profile_incomplete` edge is what waits for them.
   */
  emailVerifiedAt: z.iso.datetime().nullable(),
  phoneVerifiedAt: z.iso.datetime().nullable(),
  lastActiveAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
});

export type User = z.infer<typeof UserSchema>;

/**
 * The staff-facing detail view: the lifecycle context behind a status.
 * `suspendedUntil` is what the auto-restore job and the login-time lazy check
 * read; `statusReason` is the human explanation shown to the member.
 */
export const UserDetailSchema = UserSchema.extend({
  statusReason: z.string().nullable(),
  suspendedUntil: z.iso.datetime().nullable(),
  roles: z.array(z.string()),
  /**
   * The protected founder (N28) — changeable only by themselves.
   *
   * A read model, not a permission: the staff list needs it to render that row
   * without Suspend and Ban rather than offering buttons the api is certain to
   * refuse. The api stays the authority either way.
   */
  isFounder: z.boolean(),
  /**
   * The AI-generated-photo advisories (P27b1) for the member's **current**
   * profile photos, in the member's own photo order. Empty for a staff account
   * and for a member whose photos have not been checked. Staff-only by
   * construction: nothing member-facing extends this shape.
   */
  photoAdvisories: z.array(PhotoAdvisorySchema),
  /**
   * The member's current profile photos, in their own order — what
   * `photoAdvisories` is keyed against (P27b2), so a photo not yet checked is
   * drawn as "not checked" rather than not drawn at all. Descriptors only; the
   * bytes are a separate `media.review` mint (N12).
   */
  photos: z.array(MemberPhotoSchema),
});

export type UserDetail = z.infer<typeof UserDetailSchema>;
